import React from "react";
import { useTranslation } from "react-i18next";
import { Maturity, PromptPack } from "../../../../types";
import CustomCheckButtons from "../../../common/check-buttons/custom-check-buttons";
import { ModeFlags } from "../../../../hooks/game-settings/useGameSettings";

interface PromptSettings {
  forcedPrompts: boolean;
  multiPrompt: boolean;
  prompts: Set<PromptPack>;
}

interface PromptSettingsProps {
  settings: PromptSettings;
  onChange: (settings: Partial<PromptSettings>) => void;
  maturity: Maturity;
  userPremium: boolean;
  modeFlags: ModeFlags;
}

const PromptSettings: React.FC<PromptSettingsProps> = ({
  settings,
  onChange,
  maturity,
  userPremium,
  modeFlags,
}) => {
  const { t } = useTranslation(["game-settings", "common"]);

  const isSolo = !!modeFlags.solo_writing;

  const maturePrompts = [
    { label: t("prompts.basic", { ns: "common" }), value: PromptPack.BASIC },
    {
      label: t("prompts.hardcore", { ns: "common" }),
      value: PromptPack.HARDCORE,
    },
    { label: t("prompts.drama", { ns: "common" }), value: PromptPack.DRAMA },
    { label: t("prompts.art", { ns: "common" }), value: PromptPack.ART },
    {
      label: t("prompts.fantasy", { ns: "common" }),
      value: PromptPack.FANTASY,
    },
    { label: t("prompts.fluff", { ns: "common" }), value: PromptPack.FLUFF },
    { label: t("prompts.gamers", { ns: "common" }), value: PromptPack.GAMERS },
    { label: t("prompts.hungry", { ns: "common" }), value: PromptPack.HUNGRY },
    {
      label: t("prompts.phrases", { ns: "common" }),
      value: PromptPack.PHRASES,
    },
  ];

  const handlePromptSelect = (val: PromptPack, checked: boolean) => {
    onChange({
      prompts: new Set(
        checked
          ? [...settings.prompts, val]
          : [...settings.prompts].filter((p) => p !== val)
      ),
    });
  };

  return (
    <CustomCheckButtons
      yesLabel={t("body.forcedPromptsForced")}
      noLabel={t("body.forcedPromptsOptional")}
      multiPromptsLabel={t("body.multiPrompt")}
      forcedPrompts={settings.forcedPrompts}
      hideForcedPrompts={isSolo}
      multiPrompts={settings.multiPrompt}
      onMultiPrompts={(value) => onChange({ multiPrompt: value })}
      yesMulti={t("body.multiPromptYes")}
      noMulti={t("body.multiPromptNo")}
      onForcePrompt={(value) => onChange({ forcedPrompts: value })}
      selected={settings.prompts}
      disabled={(val) =>
        (maturity === Maturity.ALL_AGES && val === PromptPack.HARDCORE) ||
        (!userPremium && val > PromptPack.HARDCORE)
      }
      onSelect={handlePromptSelect}
      label={t("body.promptPack")}
      subLabel={t("body.promptPack2")}
      options={maturePrompts}
    />
  );
};

export default PromptSettings;
export type { PromptSettings, };
